import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Lozenge from "@atlaskit/lozenge";
import Button from "@atlaskit/button/new";
import { fetchFlowRunnerStatus } from "../../../../redux/actions/integrationActions";
import { formatRunTimestamp, getStatusAppearance } from "../../status/flowStatusUtils";

const formatBytes = (bytes) => {
  if (bytes === undefined || bytes === null || Number.isNaN(bytes)) {
    return "—";
  }
  const absBytes = Math.abs(bytes);
  if (absBytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatMs = (ms) => {
  if (ms === undefined || ms === null || Number.isNaN(ms)) {
    return "—";
  }
  return `${Math.round(ms)} ms`;
};

const FlowStatusPage = () => {
  const dispatch = useDispatch();
  const { flowId } = useParams();
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const flowRunnerStatuses = useSelector((state) => state.integration.flowRunnerStatuses || {});
  const statuses = flowId ? flowRunnerStatuses[flowId] : undefined;
  const hasLoadedStatuses = flowId ? Object.prototype.hasOwnProperty.call(flowRunnerStatuses, flowId) : false;
  const statusList = Array.isArray(statuses) ? statuses : [];
  const isLoading = flowId && !hasLoadedStatuses;

  useEffect(() => {
    if (flowId) {
      dispatch(fetchFlowRunnerStatus(flowId));
    }
  }, [dispatch, flowId]);

  if (!flowId) {
    return null;
  }

  const toggleExpandedRow = (rowKey) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  return (
    <div className="ihub-flow-status-page">
      <h4>Run History</h4>
      <p>Check recent executions for their status, timestamps, and messages from the Flow Runner.</p>

      {isLoading ? (
        <div className="ihub-flow-status-loading">Loading flow status...</div>
      ) : statusList.length === 0 ? (
        <span className="ihub-log-noshow">This flow has not been executed yet.</span>
      ) : (
        <div className="ihub-flow-status-table-wrapper">
          <table className="ihub-flow-status-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Started</th>
                <th>Ended</th>
                <th>Message</th>
                <th>Error</th>
                <th>Total Response Size</th>
                <th>Actions</th>
                <th>Execution ID</th>
              </tr>
            </thead>
            <tbody>
              {statusList.map((run, index) => {
                const rowKey = run.flowExecutionId || `${run.status}-${run.updatedAt}-${index}`;
                const actions = Array.isArray(run?.stats?.actions) ? run.stats.actions : [];
                const hasActions = actions.length > 0;
                return (
                  <React.Fragment key={rowKey}>
                    <tr>
                      <td>
                        <Lozenge appearance={getStatusAppearance(run.status)} maxWidth={200}>
                          {run.status}
                        </Lozenge>
                      </td>
                      <td>{formatRunTimestamp(run.startedAt)}</td>
                      <td>{formatRunTimestamp(run.endedAt)}</td>
                      <td>{run.message || "—"}</td>
                      <td>{run.errorMessage || "—"}</td>
                      <td>{formatBytes(run?.stats?.totalResponseSizeBytes)}</td>
                      <td>
                        {hasActions ? (
                          <Button appearance="subtle-link" spacing="none" onClick={() => toggleExpandedRow(rowKey)}>
                            {expandedRows.has(rowKey) ? "Hide actions" : "View actions"}
                          </Button>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="ihub-flow-status-id">{run.flowExecutionId || "—"}</td>
                    </tr>
                    {expandedRows.has(rowKey) && hasActions && (
                      <tr className="ihub-flow-status-row-expanded">
                        <td colSpan={8}>
                          <ul className="ihub-flow-status-actions">
                            {actions.map((action) => (
                              <li key={action.actionId}>
                                <strong>{action.actionName || action.actionId}</strong> — runs {action.runs}, avg {formatMs(action.averageExecutionTimeMs)}, total {formatMs(action.totalExecutionTimeMs)}, data {formatBytes(action.totalResponseSizeBytes)}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FlowStatusPage;
